import React, {useContext, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {
	Alert,
	Dialog,
	DialogActions,
	DialogTitle, InputAdornment,
	Snackbar,
	Step,
	StepContent,
	StepLabel,
	Stepper,
	Tab,
	Tabs,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import {
	getCategoriesStatus, getItemCategories, postNewProductGroup,
	postNewProductItem,
	selectGetCategoriesById
} from "../../redux/ProductsSlice";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import ValidatedTextField from "../../Components/Inputs/ValidatedTextField";
import {alphaNumericEmptyValidator, alphaNumericValidator, checkAllValid} from "../../Helpers/ValidationHelpers";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {Link} from "react-router-dom";
import TrimObjectStrings from "../../Helpers/TrimObjectStrings";
import {IsOnboardedContext} from "../../Contexts/IsOnboardedContext";
import CategoriesAutocomplete from "../../Components/Products/Inputs/CategoriesAutocomplete";
import CreateProductGroupCategoryTab from "../../Components/Products/CreateProductGroupCategoryTab";
import PropTypes from "prop-types";
import {useTheme} from "@mui/material/styles";
import CreateProductReview from "../../Components/Products/CreateProductReview";
import ProductParameterDataGrid from "../../Components/Parameters/ProductParameterDataGrid";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import LoadingOverlay from "../../Components/Loading/LoadingOverlay";

function TabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`category-tabpanel-${index}`}
			aria-labelledby={`category-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ p: 2}}>
					{children}
				</Box>
			)}
		</div>
	);
}

TabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.number.isRequired,
	value: PropTypes.number.isRequired,
};

function CreateProduct() {
	useEffect(() => {
		document.title = "New Product - CPC";
	}, []);
	
	const theme = useTheme();
	const dispatch = useDispatch();
	const context = useContext(IsOnboardedContext);
	const [activeForm, setActiveForm] = useState();
	const [categoryWithNoItemsError, setCategoryWithNoItemsError] = useState(null);
	const [productId, setProductId] = useState(null);
	const [open, setOpen] = useState(false); // for submit dialogue
	const [openSnackBar, setOpenSnackbar] = useState(false); 
	const [createProductError, setCreateProductError] = useState(null);
	const [activeStep, setActiveStep] = useState(0);
	const [productType, setProductType] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [categoryTab, setCategoryTab] = useState(0);
	const [rows, setRows] = useState([]);
	
	const categories = useSelector(selectGetCategoriesById);
	const categoriesStatus = useSelector(getCategoriesStatus);

	useEffect(() => {
		if (categoriesStatus === "idle") {
			dispatch(getItemCategories());
		}
	}, [dispatch, categoriesStatus]);
	
	function handleSubmit(event) {
		event.preventDefault();
		if (checkAllValid(formValid.current)) {
			setIsSubmitting(true);
			setCreateProductError(null);
			if (productType === 'item') {
				dispatch(postNewProductItem(TrimObjectStrings({
					...activeForm,
					fields: rows,
					createdBy: context.userName,
				}
				))).unwrap()
					.then((response) => {
						setProductId(response.productItemId);
						setOpen(true);
						setIsSubmitting(false);
						setActiveForm(undefined);
						setRows([]);
					})
					.catch((error) => {
						console.error('Failed to post new product', error);
						setIsSubmitting(false);
						setCreateProductError(error)
						setOpenSnackbar(true);
					});
			} else if (productType === 'group') {
				dispatch(postNewProductGroup(TrimObjectStrings({
					...activeForm,
					createdBy: context.userName,
				}))).unwrap()
					.then((response) => {
						setProductId(response.productGroupId);
						setOpen(true);
						setIsSubmitting(false);
						setActiveForm(undefined);
					})
					.catch((error) => {
						console.error('Failed to post new product', error);
						setIsSubmitting(false);
						setCreateProductError(error)
					});
			}
		}
	}
	
	const handleResetForm = () => {
		setActiveStep(0);
		setActiveForm(undefined);
		setProductType(null);
		setCategoryWithNoItemsError(null);
		setCreateProductError(null);
		setOpenSnackbar(false);
		setOpen(false);
		setIsSubmitting(false);
	}
	
	const firstStep = {
		label: 'Is the product an Item or Group?',
		description: `Think of a Product Group as a grouping of Product Items. For instance, a car may be a Group consisting of multiple Items (e.g. a chassis, wheel, etc.). An Item does not have to belong to a Group, and can be quoted as a standalone item.`,
	};
	
	const productItemFormSteps = [
		{
			label: 'Item details',
			description: 'Enter the basic details of the product item.',
			optional: false
		},
		{
			label: 'Parameters',
			description: 'Specify parameters relevant to the design and manufacture of the product item.',
			optional: true
		}
	];

	const productGroupFormSteps = [
		{
			label: 'Group details',
			description: 'Enter the basic details of the product group.',
			optional: false
		},
		{
			label: 'Add Categories and Items',
			description: <React.Fragment>
				{'Add the child categories that make up the Group. These can be as broad or granular as you wish.'} <br />{'For instance, for a '}
				<b>{"cars"}</b> {'Group, you might want to add a '} <b>{"wheels"}</b> {' category.'}
			</React.Fragment>,
			optional: false
		},
		{
			label: 'Review',
			description: <React.Fragment>
				{"Let's sum up. You can always make changes after creation."}
			</React.Fragment>,
			optional: false
		}
	];
	
	const productFormFields = [
		{
			name: "name",
			displayName: "Name",
			initialValue: "",
			required: true,
			validator: alphaNumericValidator,
			info: <React.Fragment>
				The name of the product.
			</React.Fragment>
		},
		{
			name: "description",
			displayName: "Description",
			initialValue: "",
			required: true,
			validator: alphaNumericValidator,
			info: <React.Fragment>
				A brief description of the product.
			</React.Fragment>
		},
		{
			name: "businessCode",
			displayName: "Code",
			initialValue: "",
			required: true,
			validator: alphaNumericValidator,
			info: <React.Fragment>
				{'A unique business code representing the product. '} <em>{"e.g. ABC123"}</em>
			</React.Fragment>
		},
		{
			name: "sourceFileName",
			displayName: "Assembly File",
			initialValue: "",
			validator: alphaNumericEmptyValidator,
			info: <React.Fragment>
				{'If known, supply the assembly file name (including the extension).'} <em>{"e.g. example.asm"}</em>
			</React.Fragment>
		}
	]

	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleNextWithCategoryValidation = () => {
		const categoriesWithoutAnyItems = activeForm.categories.filter(category => category.productItems.length === 0);
		if (categoriesWithoutAnyItems.length > 0) {
			const categoryNames = categoriesWithoutAnyItems.map(category => category.name).join(", ");
			setCategoryWithNoItemsError(`Please add at least one item to categories: ${categoryNames}`);
		} else {
			setCategoryWithNoItemsError(null);
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
		}
	}

	const handleFailureSnackbarClose = (event, reason) => {
		if (reason === 'clickaway' || reason === 'backdropClick') {
			return;
		}
		setOpenSnackbar(false);
	};

	const handleBack = (step) => {
		setActiveStep((prevActiveStep) => (step !== undefined ? step : prevActiveStep - 1));
	};

	const handleClose = (event, reason) => {
		if (reason === 'clickaway' || reason === 'backdropClick') {
			return;
		}

		setOpen(false);
	};
	
	const handleProductTypeSelection = (event, newProductType) => {
		if (!newProductType) {
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
			return;
		}
		
		if (!!productType) {
			if (newProductType === "group") {
				delete formValid.current.dropdownValues;
				formValid.current.categories = {};
				setActiveForm((prevState) => ({
					...prevState,
					categories: []
				}))
			} else if (newProductType === "item") {
				delete formValid.current.categories;
				formValid.current.dropdownValues = {};
				setActiveForm((prevState) => ({
					...prevState,
					categories: []
				}))
			}
			
			setProductType(newProductType);
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
			return;
		}
		
		setProductType(newProductType);
		const formFields = productFormFields.reduce((acc, field) => {
			acc[field.name] = field.initialValue;
			return acc;
		}, {});

		setActiveForm({
			...formFields,
			categories: []
		});

		formValid.current = {
			details: {
				name: false,
				description: false,
				businessCode: false,
			},
		}
		if (newProductType === "group") {
			formValid.current.categories = {}
		} else if (newProductType === "item") {
			formValid.current.dropdownValues = {};
		} else {
			formValid.current = {};
		}

		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};
	
	const handleCategoryTabChange = (event, newValue) => {
		setCategoryTab(newValue);
	};

	const formValid = useRef({});

	return (
		<>
			{isSubmitting && <LoadingOverlay />}
			<Typography variant="h2">New Product</Typography>
			<Box
				component="form"
				onSubmit={handleSubmit}
				sx={{ 
				maxWidth: 600,
				mt: 2,
					'& .MuiTextField-root': { width: '40ch' }
			}}>
				<Stepper activeStep={activeStep} orientation="vertical">
					<Step key={firstStep.label}>
						<StepLabel
							onClick={() => handleBack(0)}
							style={{cursor: activeStep > 0 ? 'pointer' : "default"}}
							className={activeStep > 0 ? "hyperlink" : ""}
						>
							{firstStep.label}
						</StepLabel>
						<StepContent>
							<Typography>{firstStep.description}</Typography>
							<Box sx={{ my: 2 }}>
								<ToggleButtonGroup 
									value={productType}
									color="secondary"
									fullWidth={true}
									exclusive
									onChange={handleProductTypeSelection}
									aria-label="product type selection"
								>
									<ToggleButton value="item" aria-label="item">
										Item
									</ToggleButton>
									<ToggleButton value="group" aria-label="group">
										Group
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</StepContent>
					</Step>
					{ productType === "item" &&
						<Step key={productItemFormSteps[0].label}>
							<StepLabel
								onClick={() => { if (activeStep > 1) { handleBack(); } }}
								style={{cursor: activeStep > 1 ? 'pointer' : "default"}}
								className={activeStep > 1 ? "hyperlink" : ""}
							>
								{productItemFormSteps[0].label}
							</StepLabel>
							<StepContent>
								<Typography>{productItemFormSteps[0].description}</Typography>
								{productFormFields.map((field, i) => (
									<Box
										display="flex"
										justifyContent="left"
										noValidate
										sx={{ my: 1 }}
										key={i}
									>
										<ValidatedTextField
											id={field.name}
											label={field.displayName}
											validator={field.validator}
											onChange={isValid => (formValid.current.details[field.name] = isValid)}
											setActiveForm={setActiveForm}
											isSubmitting={false}
											required={field.required}
											initialValue={activeForm?.[field.name]}
											fieldPath={[field.name]}
											infoAdornment={<InputAdornment position="end">
												<Tooltip enterTouchDelay={200} disableFocusListener title={field.info}>
													<InfoIcon />
												</Tooltip>
											</InputAdornment>}
										/>
									</Box>
								))}
								<Grid size={{ xs: 12 }}>
									<Box
										display="flex"
										justifyContent="left"
										noValidate
										sx={{ my: 1 }}
									>
										<CategoriesAutocomplete 
											initialValue={activeForm?.categories} 
											setActiveForm={setActiveForm} 
											categoryOptions={categories}
											formValid={formValid}
											createNewOptionsAllowed
										/>
									</Box>
								</Grid>
								<Box sx={{ mb: 2 }}>
									<Button
										variant="contained"
										onClick={handleNext}
										sx={{ mt: 1, mr: 1 }}
										disabled={!checkAllValid(formValid.current.details)}
									>
										Continue
									</Button>
									<Button
										onClick={() => handleBack(0)}
										sx={{ mt: 1, mr: 1 }}
									>
										Back
									</Button>
								</Box>
							</StepContent>
						</Step>
					}
					{ productType === "item" &&
						<Step key={productItemFormSteps[1].label}>
							<StepLabel optional={<Typography variant="caption">Optional</Typography>}
							>
								{productItemFormSteps[1].label}
							</StepLabel>
							<StepContent>
								<Typography>{productItemFormSteps[1].description}</Typography>
								<Grid container
									  spacing={2}
									  sx={{my: 2}}
								>
									<ProductParameterDataGrid 
										rows={rows} 
										setRows={setRows}
										isEditMode
										minWidth={{ md: 800, lg: 1150}} />
								</Grid>
								<Box sx={{ mb: 2 }}>
									<Button
										variant="contained"
										onClick={handleSubmit}
										sx={{ mt: 1, mr: 1 }}
										disabled={!checkAllValid(formValid.current) || isSubmitting}
									>
										{isSubmitting ? "Submitting..." : "Finish"}
									</Button>
									<Button
										onClick={() => handleBack()}
										sx={{ mt: 1, mr: 1 }}
										disabled={isSubmitting}
									>
										Back
									</Button>
								</Box>
							</StepContent>
						</Step>
					}
					{ productType === "group" &&
						<Step key={productGroupFormSteps[0].label}>
							<StepLabel
								onClick={() => { if (activeStep > 1) { handleBack(1); } }}
								style={{cursor: activeStep > 1 ? 'pointer' : "default"}}
								className={activeStep > 1 ? "hyperlink" : ""}
							>
								{productGroupFormSteps[0].label}
							</StepLabel>
							<StepContent>
								<Typography>{productGroupFormSteps[0].description}</Typography>
								{ productFormFields.map((field, i) => (
									<Grid size={{ xs: 12 }} key={i}>
										<Box
											display="flex"
											justifyContent="left"
											noValidate
											sx={{ my: 1 }}
										>
											<ValidatedTextField
												id={field.name}
												label={field.displayName}
												validator={field.validator}
												onChange={isValid => (formValid.current.details[field.name] = isValid)}
												setActiveForm={setActiveForm}
												isSubmitting={false}
												initialValue={activeForm?.[field.name]}
												fieldPath={[field.name]}
												infoAdornment={<InputAdornment position="end">
													<Tooltip disableFocusListener title={field.info}>
														<InfoIcon />
													</Tooltip>
												</InputAdornment>}
												required={field.required}
											/>
										</Box>
									</Grid>
								))}
								<Box sx={{ mb: 2 }}>
									<Button
										variant="contained"
										onClick={handleNext}
										sx={{ mt: 1, mr: 1 }}
										disabled={!checkAllValid(formValid.current)}
									>
										Continue
									</Button>
									<Button
										onClick={() => handleBack(0)}
										sx={{ mt: 1, mr: 1 }}
									>
										Back
									</Button>
								</Box>
							</StepContent>
						</Step>
					}
					{ productType === "group" &&
						<Step key={productGroupFormSteps[1].label}>
							<StepLabel
								onClick={() => { if (activeStep > 2) { handleBack(); } }}
								style={{cursor: activeStep > 2 ? 'pointer' : "default"}}
								className={activeStep > 2 ? "hyperlink" : ""}
							>
								{productGroupFormSteps[1].label}
							</StepLabel>
							<StepContent>
								<Typography>{productGroupFormSteps[1].description}</Typography>
								<Box
									display="flex"
									justifyContent="left"
									noValidate
									sx={{ my: 2 }}
								>
									<CategoriesAutocomplete 
										initialValue={activeForm?.categories}
										setActiveForm={setActiveForm} 
										categoryOptions={categories}
										formValid={formValid}
										createNewOptionsAllowed={false}
									/>
								</Box>
								{activeForm?.categories.length > 0 && (
									<>
										<Box>
											{'Finally, assign the Product Items that you wish to be quotable for each category. You can assign all items that fall under that category, or choose specific items.'} <br /><br />{'As an example, you might supply many different sized wheels which all fall under a '}
											<b>{"wheels"}</b> {'category, but smaller wheels might not be appropriate to quote for a '}  <b>{"Truck"}</b> {' Product Group.'}
										</Box>
										<Box
											sx={{ my: 2 }}
										>
											<Tabs
												value={categoryTab}
												onChange={handleCategoryTabChange}
												aria-label="category tabs"
												variant="scrollable"
												scrollButtons
												allowScrollButtonsMobile
											>
												{activeForm?.categories?.map((category, i) => (
													<Tab value={i} label={category.name} key={i} wrapped/>
												))}
											</Tabs>
											{activeForm?.categories?.map((category, i) => (
												<TabPanel 
													value={categoryTab} 
													index={i}
													dir={theme.direction}
													key={i}
												>
													<CreateProductGroupCategoryTab
														categories={categories}
														setActiveForm={setActiveForm}
														activeCategory={category}
													/>
												</TabPanel>
											))}
										</Box>
										<Box>
											{categoryWithNoItemsError &&
												<Alert variant="filled" severity="warning">
													{categoryWithNoItemsError}
												</Alert>
											}
										</Box>
										<Box sx={{ mb: 2 }}>
											<Button
												variant="contained"
												onClick={handleNextWithCategoryValidation}
												sx={{ mt: 1,
													mr: 1,
												}}
												disabled={!checkAllValid(formValid.current)}
											>
												Continue
											</Button>
											<Button
												onClick={() => handleBack()}
												sx={{ mt: 1, mr: 1 }}
											>
												Back
											</Button>
										</Box>
									</>
									)
								}
								
							</StepContent>
						</Step>
					}
					{ productType === "group" &&
						<Step key={productGroupFormSteps[2].label}>
							<StepLabel optional={productGroupFormSteps[2].optional
								? <Typography variant="caption">Optional</Typography>
								: null}
							>
								{productGroupFormSteps[2].label}
							</StepLabel>
							<StepContent>
								<Typography>{productGroupFormSteps[2].description}</Typography>
								<Box
									display="flex"
									justifyContent="left"
									sx={{ my: 2 }}
								>
									<CreateProductReview activeForm={activeForm} />
								</Box>
								<Box sx={{ mb: 2 }}>
									<Button
										variant="contained"
										onClick={handleSubmit}
										sx={{ mt: 1, mr: 1 }}
										disabled={!checkAllValid(formValid.current) || isSubmitting}
									>
										{isSubmitting ? "Submitting..." : "Finish"}
									</Button>
									<Button
										onClick={() => handleBack()}
										sx={{ mt: 1, mr: 1 }}
										disabled={isSubmitting}
									>
										Back
									</Button>
								</Box>
							</StepContent>
						</Step>
					}
				</Stepper>
			</Box>
			<Dialog
				open={open}
				keepMounted
				onClose={handleClose}
				aria-describedby="alert-successful-product-creation"
			>
				<DialogTitle>
					<Box display="flex" alignItems="center">
						<CheckCircleIcon color="success" fontSize="large" sx={{
							marginRight: '15px'
						}}/>
						<Typography variant="h6">{`Product successfully created!`}</Typography>
					</Box>
				</DialogTitle>
				<DialogActions>
					<Link to={`/`}>
						<Button onClick={handleClose}>
							{`Home`}
						</Button>
					</Link>
					<Button onClick={handleResetForm}>
						{`Create another`}
					</Button>
					<Link to={`/Products/${productId}`} underline="none">
						<Button onClick={handleClose} variant="contained" color="primary">
							{`Go to product`}
						</Button>
					</Link>
				</DialogActions>
			</Dialog>
			<Snackbar open={openSnackBar}
					  onClose={handleFailureSnackbarClose}
					  autoHideDuration={3000}
			>
				<Alert onClose={handleFailureSnackbarClose}
					   severity="error"
				>
					{createProductError?.status === 409 ? `Failed to create product. ${createProductError?.detail}.` : "Failed to create product. Please try again."}
				</Alert>
			</Snackbar>
		</>
	);
}

export default CreateProduct;
